import { flatten, lt, map, pathOr, pluck, reduce, sum } from "ramda";
import formatMoney from "sig/formatters/money";

import type {
  ICartItem,
  ICartModifier,
  ICartSide,
  ISelectedModifier,
} from "types/cart";
import type { IDetailedTotals, ISubtotalDetail } from "types/totals";

function calculateSubtotalForMods(item: ICartItem): ISubtotalDetail[] {
  const selectedModifiers: ISelectedModifier[] = item.selectedModifiers;
  const modifiers: ICartModifier[] = flatten(pluck("mods", selectedModifiers));
  return map(
    (mod: ICartModifier) => ({
      name: mod.name,
      subtotal: mod.price * item.quantity,
    }),
    modifiers
  );
}

// Client side tax calculation will soon be deprecated.
// See interfaces/caltulate_totals for backend calculation API.
function calculateTaxesForMods(item: ICartItem) {
  const selectedModifiers: ISelectedModifier[] = item.selectedModifiers;
  const modifiers: ICartModifier[] = flatten(pluck("mods", selectedModifiers));
  return sum(
    map((mod: ICartModifier) => {
      return mod.taxRate
        ? mod.price * parseFloat(mod.taxRate.percentageRate) * item.quantity
        : 0;
    }, modifiers)
  );
}

function calculateSubtotalForSides(item: ICartItem): ISubtotalDetail[] {
  return map(
    (side: ICartSide) => ({
      name: side.name,
      subtotal: side.price * item.quantity,
    }),
    item.selectedSides
  );
}

// Client side tax calculation will soon be deprecated.
// See interfaces/caltulate_totals for backend calculation API.
function calculateTaxesForSides(item: ICartItem) {
  const sideTaxes: number[] = map(
    (side: ICartSide) =>
      side.price * parseFloat(side.taxRate.percentageRate) * item.quantity,
    item.selectedSides
  );
  return sum(sideTaxes);
}

function formatPrice(priceCents: number): string {
  return formatMoney(priceCents * 0.01);
}

function formatSignedPrice(priceCents: number): string {
  const formattedPrice = formatPrice(priceCents);
  if (lt(priceCents, 0)) {
    return formattedPrice;
  }
  return `+${formattedPrice}`;
}

function getItemDetailedTotals(item: ICartItem): IDetailedTotals {
  const base = item.quantity * item.price;
  const mods = calculateSubtotalForMods(item);
  const sides = calculateSubtotalForSides(item);
  return {
    base,
    mods,
    sides,
  };
}

function getItemSubtotal(item: ICartItem): number {
  const detailed = getItemDetailedTotals(item);
  return (
    detailed.base +
    sum(map((detail: ISubtotalDetail) => detail.subtotal, detailed.mods)) +
    sum(map((detail: ISubtotalDetail) => detail.subtotal, detailed.sides))
  );
}

function getItemTotals(item: ICartItem) {
  const subtotal = getItemSubtotal(item);

  // Client side tax calculation will soon be deprecated.
  // See interfaces/caltulate_totals for backend calculation API.
  let tax =
    pathOr(0, ["taxRate", "percentageRate"], item) *
    (item.quantity * item.price);
  tax += calculateTaxesForMods(item);
  tax += calculateTaxesForSides(item);
  // @todo add modifier and sides totals and taxes here
  return { subtotal, tax };
}

function getTotals(
  items: ICartItem[],
  deliveryFee = 0,
  deliveryFeeTaxRate: string | number = 0
) {
  const deliveryFeeTaxRateNum =
    typeof deliveryFeeTaxRate === "string"
      ? parseFloat(deliveryFeeTaxRate)
      : deliveryFeeTaxRate;
  const deliveryTax = deliveryFee * deliveryFeeTaxRateNum;

  return reduce<ICartItem, { subtotal: number; tax: number }>(
    (acc, item) => {
      const { subtotal: itemSubtotal, tax: itemTax } = getItemTotals(item);

      return {
        subtotal: acc.subtotal + itemSubtotal,
        tax: acc.tax + itemTax,
      };
    },
    { subtotal: 0, tax: deliveryTax },
    items
  );
}

export {
  formatSignedPrice,
  formatPrice,
  getTotals,
  getItemDetailedTotals,
  getItemTotals,
  getItemSubtotal,
};
export default getTotals;
